import { listFromApiResponse } from "utils/functionsApi";

const UNSELECTED = -1;

function normalizeUbicationId(id) {
	if (id == null || id === "" || id === UNSELECTED) return null;
	const n = Number(id);
	return Number.isFinite(n) && n > 0 ? n : null;
}

function unwrapAssignedUbications(assignedResponse) {
	if (!assignedResponse) return [];
	if (Array.isArray(assignedResponse)) return assignedResponse;
	if (
		typeof assignedResponse === "object" &&
		(assignedResponse.ubication || assignedResponse.id != null)
	) {
		return [assignedResponse];
	}
	return listFromApiResponse(assignedResponse);
}

function pickAssignedUbications(resp) {
	const employee = resp.employee || {};
	const buckets = [
		employee.employee_with_ubications,
		employee.employee_ubications,
		employee.ubications,
		employee.ubicaciones,
		employee.assigned_ubications,
		employee.employee_with_function?.ubications,
		resp.employee_with_ubications,
		resp.employee_ubications,
		resp.ubications,
		resp.ubicaciones,
	];

	const singular = [
		employee.employee_with_ubication,
		resp.employee_with_ubication,
	];

	let merged = [];
	for (const bucket of buckets) {
		merged = merged.concat(unwrapAssignedUbications(bucket));
	}
	for (const item of singular) {
		merged = merged.concat(unwrapAssignedUbications(item));
	}

	return merged;
}

function pickSelectableUbications(resp) {
	const employee = resp.employee || {};
	const candidates = [
		resp.select_ubications,
		employee.select_ubications,
		employee.selectable_ubications,
		resp.selectable_ubications,
	];

	for (const candidate of candidates) {
		const list = unwrapAssignedUbications(candidate);
		if (list.length > 0) return list;
	}

	return [];
}

/**
 * Assigned employee ubications. Does not fall back to the company catalog
 * unless fallbackToSelectable is true (unused on clock-in write surfaces).
 * @returns {{ id: number, name: string, is_default?: boolean }[]}
 */
export function toSelectOptions(
	assignedResponse,
	selectableList,
	{ fallbackToSelectable = false } = {}
) {
	const assigned = unwrapAssignedUbications(assignedResponse);
	const selectable = Array.isArray(selectableList)
		? selectableList
		: listFromApiResponse(selectableList);

	const mapAssigned = (item) => {
		const ub = item.ubication || item;
		return {
			id: ub.id,
			name: ub.name,
			is_default: Boolean(item.is_default),
		};
	};

	const mapSelectable = (item) => ({
		id: item.id,
		name: item.name,
		is_default: Boolean(item.is_default),
	});

	const source =
		assigned.length > 0
			? assigned.map(mapAssigned)
			: fallbackToSelectable
				? selectable.map(mapSelectable)
				: [];

	return dedupeSelectOptions(
		source.filter((opt) => opt.id != null && opt.name != null)
	);
}

function dedupeSelectOptions(options) {
	const byId = new Map();

	for (const opt of options) {
		const existing = byId.get(opt.id);
		if (!existing) {
			byId.set(opt.id, { ...opt });
			continue;
		}
		if (opt.is_default) {
			byId.set(opt.id, { ...existing, is_default: true });
		}
	}

	return Array.from(byId.values());
}

export function defaultUbicationId(options, existingId) {
	const normalizedExisting = normalizeUbicationId(existingId);
	if (normalizedExisting != null) return normalizedExisting;

	const defaultOpt = (options || []).find((opt) => opt.is_default);
	if (defaultOpt) return defaultOpt.id;

	if ((options || []).length === 1) return options[0].id;

	return UNSELECTED;
}

export function resolveUbicationId(time) {
	if (!time || typeof time !== "object") return null;
	if (time.ubication && time.ubication.id != null) {
		return normalizeUbicationId(time.ubication.id);
	}
	return normalizeUbicationId(time.ubication_id);
}

/**
 * POST (echo=false): omit ubication_id when unselected.
 * PUT (echo=true): always send ubication_id as number or null.
 */
export function withUbicationId(payload, timeOrId, { echo = false } = {}) {
	const rawId =
		typeof timeOrId === "object" && timeOrId !== null
			? resolveUbicationId(timeOrId)
			: normalizeUbicationId(timeOrId);

	const result = { ...payload };

	if (echo) {
		result.ubication_id = rawId;
		return result;
	}

	if (rawId != null) {
		result.ubication_id = rawId;
	} else {
		delete result.ubication_id;
	}

	return result;
}

export function optionsFromWebTerminal(resp) {
	if (!resp || typeof resp !== "object") return [];

	return toSelectOptions(pickAssignedUbications(resp), pickSelectableUbications(resp));
}
